'use client';

import { ArrowRight, Download, Loader2, Sparkles, Wand2 } from 'lucide-react';
import React, { useState } from 'react';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { ImageUploader } from './ImageUploader';
import { PropertyImagePicker } from './PropertyImagePicker';
import { SelectionCard } from './SelectionCard';
import { StepLayout } from './StepLayout';
import type { SpacesWizardProps, WizardState } from './types';
import {
    ArtworkStyle,
    ClutterLevel,
    ColourPalette,
    DesignStyle,
    RoomType,
    SPACES_MAX_USAGE,
    TimeOfDay,
} from './types';

export const SpacesWizard: React.FC<SpacesWizardProps> = ({
  mode = 'standalone',
  wishlistProperties = [],
  usageCount = 0,
  maxUsage = SPACES_MAX_USAGE,
  onUsageIncrement,
  onUsageSync,
}) => {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [data, setData] = useState<WizardState>({
    style: null,
    colourPalette: null,
    clutter: null,
    timeOfDay: null,
    artwork: null,
    roomType: null,
    additionalNotes: '',
    image: null,
  });

  const isPortal = mode === 'portal';
  const remaining = Math.max(0, maxUsage - usageCount);
  const canGenerateUsage = remaining > 0;

  const TOTAL_STEPS = 7;

  const updateData = (key: keyof WizardState, value: WizardState[keyof WizardState]) => {
    setData(prev => ({ ...prev, [key]: value }));
  };

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const handleGenerate = async () => {
    if (isPortal && !canGenerateUsage) {
      setError('You have reached the maximum number of room designs for your account.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      const response = await fetch('/api/spaces/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: data }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate design');
      }

      const result = await response.json();
      
      if (result.success && result.imageUrl) {
        setGeneratedImage(result.imageUrl);
        if (typeof result.remainingUsage === 'number') {
          onUsageSync?.(maxUsage - result.remainingUsage);
        } else {
          onUsageIncrement?.();
        }
        nextStep();
      } else {
        throw new Error(result.error || 'Failed to generate design');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to generate design. Please try again.";
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!data.roomType;
      case 2: return !!data.style;
      case 3: return !!data.colourPalette;
      case 4: return !!data.clutter;
      case 5: return !!data.timeOfDay;
      case 6: return !!data.artwork;
      case 7: return !!data.image;
      default: return true;
    }
  };

  const renderRoomType = () => (
    <StepLayout title="The Foundation" subtitle="What type of room are we redesigning today?" >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.values(RoomType).map((type) => (
          <SelectionCard 
            key={type} 
            label={type} 
            selected={data.roomType === type} 
            onClick={() => updateData('roomType', type)} 
          />
        ))}
      </div>
    </StepLayout>
  );

  const renderStyle = () => (
    <StepLayout title="The Aesthetic" subtitle="Choose the architectural style that inspires you." onBack={prevStep}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Object.values(DesignStyle).map((style) => (
          <SelectionCard 
            key={style} 
            label={style} 
            selected={data.style === style} 
            onClick={() => updateData('style', style)} 
          />
        ))}
      </div>
    </StepLayout>
  );

  const renderColourPalette = () => (
    <StepLayout title="The Palette" subtitle="Select the colour story for your space." onBack={prevStep}>
       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.values(ColourPalette).map((palette) => (
          <SelectionCard 
            key={palette} 
            label={palette.split('(')[0]?.trim() || palette} 
            selected={data.colourPalette === palette} 
            onClick={() => updateData('colourPalette', palette)} 
          />
        ))}
      </div>
    </StepLayout>
  );

  const renderClutter = () => (
    <StepLayout title="The Atmosphere" subtitle="How lived-in do you want the space to feel?" onBack={prevStep}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.values(ClutterLevel).map((level) => (
          <SelectionCard 
            key={level} 
            label={level} 
            selected={data.clutter === level} 
            onClick={() => updateData('clutter', level)} 
          />
        ))}
      </div>
    </StepLayout>
  );

  const renderLighting = () => (
    <StepLayout title="The Lighting" subtitle="Choose the time of day and lighting mood." onBack={prevStep}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Object.values(TimeOfDay).map((time) => (
          <SelectionCard 
            key={time} 
            label={time} 
            selected={data.timeOfDay === time} 
            onClick={() => updateData('timeOfDay', time)} 
          />
        ))}
      </div>
    </StepLayout>
  );

  const renderArtwork = () => (
    <StepLayout title="The Art" subtitle="What kind of art should adorn the walls?" onBack={prevStep}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.values(ArtworkStyle).map((art) => (
          <SelectionCard 
            key={art} 
            label={art.split('(')[0]?.trim() || art} 
            selected={data.artwork === art} 
            onClick={() => updateData('artwork', art)} 
          />
        ))}
      </div>
    </StepLayout>
  );

  const renderImageSelection = () => (
    <StepLayout
      title="The Canvas"
      subtitle={
        isPortal
          ? 'Choose a room photo from your saved properties.'
          : 'Upload your room and add final touches.'
      }
      onBack={prevStep}
    >
      <div className="max-w-3xl mx-auto space-y-8">
        {isPortal ? (
          <PropertyImagePicker
            wishlistProperties={wishlistProperties}
            currentImage={data.image}
            onImageSelected={(img) => updateData('image', img)}
          />
        ) : (
          <ImageUploader 
            currentImage={data.image} 
            onImageSelected={(img) => updateData('image', img)} 
          />
        )}
        
        <div>
           <h3 className="text-xl text-stone-300 mb-4 font-serif">Any specific requests? (Optional)</h3>
           <textarea
             className="w-full bg-stone-900 border border-stone-700 rounded-xl p-4 text-white placeholder-stone-500 focus:outline-none focus:border-gold-500 resize-none h-24"
             placeholder="e.g. Keep the original wooden floor, I love emerald green accents, make it feel cosy..."
             value={data.additionalNotes}
             onChange={(e) => updateData('additionalNotes', e.target.value)}
           />
        </div>

        {data.image && (
          <div className="flex flex-col items-center">
             {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-6 py-4 rounded-xl mb-6 max-w-lg text-center">
                  {error}
                </div>
             )}

             {isPortal && !canGenerateUsage && (
               <div className="bg-stone-800 border border-stone-600 text-stone-300 px-6 py-4 rounded-xl mb-6 max-w-lg text-center">
                 <Sparkles size={20} className="inline mr-2 text-gold-500" />
                 You have used all {maxUsage} of your room designs.
               </div>
             )}

            <button
              onClick={handleGenerate}
              disabled={isGenerating || (isPortal && !canGenerateUsage)}
              style={{
                background: 'linear-gradient(to right, #D6B754, #E0C97D)',
              }}
              className={`
                group relative px-10 py-5 rounded-full 
                text-stone-950 font-bold text-xl shadow-[0_0_40px_rgba(214,183,84,0.5)]
                hover:shadow-[0_0_60px_rgba(224,201,125,0.7)] transition-all transform hover:-translate-y-1
                hover:brightness-110
                disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:hover:brightness-100
              `}
            >
              {isGenerating ? (
                <span className="flex items-center gap-3">
                  <Loader2 className="animate-spin" /> Designing Your Space...
                </span>
              ) : (
                <span className="flex items-center gap-3">
                  <Wand2 className="group-hover:rotate-12 transition-transform" /> Generate Design
                </span>
              )}
            </button>

            {isPortal && canGenerateUsage && (
              <p className="mt-4 text-stone-500 text-sm">
                {remaining} of {maxUsage} designs remaining
              </p>
            )}
            {!isPortal && (
              <p className="mt-4 text-stone-500 text-sm">Powered by Route Royal</p>
            )}
          </div>
        )}
      </div>
    </StepLayout>
  );

  const renderResult = () => (
    <StepLayout title="Your Dream Space" subtitle="Drag the slider to compare the transformation." onBack={() => { setGeneratedImage(null); setStep(TOTAL_STEPS); }}>
       <div className="flex flex-col items-center w-full">
          <div className="w-full h-[65vh] mb-8 shadow-2xl rounded-xl overflow-hidden border-2 border-stone-800 bg-black/50">
             {data.image && generatedImage && (
               <BeforeAfterSlider original={data.image} generated={generatedImage} />
             )}
          </div>

          <div className="flex flex-col md:flex-row gap-6 items-center w-full justify-between max-w-4xl px-4">
             <button 
                onClick={() => {
                  setData({
                    style: null,
                    colourPalette: null,
                    clutter: null,
                    timeOfDay: null,
                    artwork: null,
                    roomType: null,
                    additionalNotes: '',
                    image: null,
                  });
                  setGeneratedImage(null);
                  setStep(1);
                }}
                className="text-stone-400 hover:text-white font-medium hover:underline underline-offset-4 decoration-stone-700 hover:decoration-white transition-all order-2 md:order-1"
             >
               Start a New Project
             </button>

             <a 
               href={generatedImage!} 
               download="royale-spaces-design.png"
               className="order-1 md:order-2 bg-gold-600 hover:bg-gold-500 text-stone-950 font-bold py-3 px-8 rounded-full flex items-center gap-2 transition-transform transform hover:scale-105 shadow-lg"
             >
               <Download size={20} /> Download Result
             </a>
          </div>
       </div>
    </StepLayout>
  );

  return (
    <div className="min-h-screen bg-stone-950 flex flex-col">
      {/* Portal usage badge */}
      {isPortal && step <= TOTAL_STEPS && (
        <div className="flex justify-end px-4 pt-4">
          <div className="bg-stone-900/80 backdrop-blur-sm border border-stone-700 rounded-full px-4 py-2 flex items-center gap-2">
            <Sparkles size={14} className="text-gold-500" />
            <span className="text-stone-300 text-xs font-medium">
              {remaining} / {maxUsage} designs left
            </span>
          </div>
        </div>
      )}

      <main className="grow flex items-center justify-center relative overflow-y-auto">
        {step === 1 && renderRoomType()}
        {step === 2 && renderStyle()}
        {step === 3 && renderColourPalette()}
        {step === 4 && renderClutter()}
        {step === 5 && renderLighting()}
        {step === 6 && renderArtwork()}
        {step === 7 && renderImageSelection()}
        {step === 8 && renderResult()}
      </main>

      {step < TOTAL_STEPS && (
        <footer className="p-6 md:p-8 border-t border-stone-900 bg-stone-950 sticky bottom-0 z-50">
           <div className="max-w-4xl mx-auto flex justify-between items-center">
             <div className="flex gap-1">
                {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-1.5 rounded-full transition-all duration-300 ${i + 1 === step ? 'w-8 bg-gold-500' : i + 1 < step ? 'w-4 bg-gold-900' : 'w-4 bg-stone-800'}`} 
                  />
                ))}
             </div>

             <button
               onClick={nextStep}
               disabled={!canProceed()}
               className={`
                 flex items-center gap-2 px-8 py-3 rounded-full font-medium transition-all
                 ${canProceed() 
                   ? 'bg-white text-stone-950 hover:bg-gold-100 hover:scale-105' 
                   : 'bg-stone-800 text-stone-500 cursor-not-allowed'}
               `}
             >
               Next Step <ArrowRight size={18} />
             </button>
           </div>
        </footer>
      )}
    </div>
  );
};
